import * as settingsApi from './settingsApi';

let cache: Record<string, any> = {};

export async function isRecaptchaEnabled(): Promise<boolean> {
  if (cache.recaptcha !== undefined) return cache.recaptcha;
  try {
    const res = await settingsApi.get('features.recaptcha');
    if (res?.success && res.data) {
      cache.recaptcha = Boolean(res.data.value);
      return cache.recaptcha;
    }
  } catch {}
  const env = String((import.meta as any).env?.VITE_ENABLE_RECAPTCHA || '').toLowerCase();
  cache.recaptcha = env === 'true';
  return cache.recaptcha;
}

export function clearFlagsCache() {
  cache = {};
}

// Force re-validation of a specific flag
export function invalidateFlag(flagName: string) {
  if (flagName === 'recaptcha') {
    delete cache.recaptcha;
  } else if (flagName === 'services') {
    delete cache.services;
  } else if (flagName === 'tentacle') {
    delete cache.tentacle;
  } else if (flagName === 'tentacle.forceMobile') {
    delete cache.tentacleForceMobile;
  }
}

export async function isServicesEnabled(): Promise<boolean> {
  if (cache.services !== undefined) return cache.services;
  try {
    const res = await settingsApi.get('features.services');
    if (res?.success && res.data) {
      cache.services = Boolean(res.data.value);
      return cache.services;
    }
  } catch {}
  cache.services = false; // fallback
  return cache.services;
}

export async function isTentacleEnabled(): Promise<boolean> {
  if (cache.tentacle !== undefined) return cache.tentacle;
  try {
    const res = await settingsApi.get('features.tentacle');
    if (res?.success && res.data) {
      cache.tentacle = Boolean(res.data.value);
      return cache.tentacle;
    }
  } catch {}
  cache.tentacle = true; // default fallback: enabled
  return cache.tentacle;
}

export async function isTentacleForceMobileEnabled(): Promise<boolean> {
  if (cache.tentacleForceMobile !== undefined) return cache.tentacleForceMobile;
  try {
    const res = await settingsApi.get('features.tentacle.forceMobile');
    if (res?.success && res.data) {
      cache.tentacleForceMobile = Boolean(res.data.value);
      return cache.tentacleForceMobile;
    }
  } catch {}
  cache.tentacleForceMobile = false;
  return cache.tentacleForceMobile;
}

export async function isSecurityLogEnabled(): Promise<boolean> {
  if (cache.securityLog !== undefined) return cache.securityLog;
  try {
    const res = await settingsApi.get('features.securityLog');
    if (res?.success && res.data) {
      cache.securityLog = Boolean(res.data.value);
      return cache.securityLog;
    }
  } catch {}
  // Par défaut, activez-le en production, désactivez-le en développement
  cache.securityLog = import.meta.env.PROD;
  return cache.securityLog;
}

export interface SphereSettings {
  rotationSpeed: number;
  size: number;
  iconSize: number;
  showNames: boolean;
  categoryHalo: boolean;
}

const SPHERE_DEFAULTS: SphereSettings = {
  rotationSpeed: 1,
  size: 460,
  iconSize: 28,
  showNames: true,
  categoryHalo: false,
};

/**
 * Récupère les réglages de la sphère de compétences (clé/valeur app_settings).
 * Retourne des valeurs par défaut si l'API échoue.
 */
export async function getSphereSettings(): Promise<SphereSettings> {
  if (cache.sphere !== undefined) return cache.sphere;
  const num = (res: any, def: number) => {
    const v = res?.success && res.data ? Number(res.data.value) : NaN;
    return Number.isFinite(v) ? v : def;
  };
  const bool = (res: any, def: boolean) =>
    res?.success && res.data ? Boolean(res.data.value) : def;
  try {
    const [rs, sz, is, sn, ch] = await Promise.all([
      settingsApi.get('skills.sphere.rotationSpeed'),
      settingsApi.get('skills.sphere.size'),
      settingsApi.get('skills.sphere.iconSize'),
      settingsApi.get('skills.sphere.showNames'),
      settingsApi.get('skills.sphere.categoryHalo'),
    ]);
    cache.sphere = {
      rotationSpeed: num(rs, SPHERE_DEFAULTS.rotationSpeed),
      size: num(sz, SPHERE_DEFAULTS.size),
      iconSize: num(is, SPHERE_DEFAULTS.iconSize),
      showNames: bool(sn, SPHERE_DEFAULTS.showNames),
      categoryHalo: bool(ch, SPHERE_DEFAULTS.categoryHalo),
    } as SphereSettings;
    return cache.sphere;
  } catch {
    cache.sphere = { ...SPHERE_DEFAULTS };
    return cache.sphere;
  }
}
